import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./ResourceDetailsContentBlock.css";
import { FileEmbed } from "@/modules/hub/features/ResourceLibraryV2/components/FileEmbed/FileEmbed";
import { VideoEmbed } from "@/modules/hub/features/ResourceLibraryV2/components/VideoEmbed/VideoEmbed";
import { LinkEmbed } from "@/modules/hub/features/ResourceLibraryV2/components/LinkEmbed/LinkEmbed";
import { PodcastEmbed } from "@/modules/hub/features/ResourceLibraryV2/components/PodcastEmbed/PodcastEmbed";
import { ResourceReferenceBlock } from "@/modules/hub/features/ResourceLibraryV2/components/ResourceReferenceBlock/ResourceReferenceBlock";
import { FaqBlock } from "@/modules/hub/features/ResourceLibraryV2/components/FaqBlock";
import { ColumnsBlock } from "@/modules/hub/features/ResourceLibraryV2/components/ColumnsBlock";
import { QuoteBlock } from "@/modules/hub/features/ResourceLibraryV2/components/QuoteBlock";
import { HeadingBlock } from "@/modules/hub/features/ResourceLibraryV2/components/HeadingBlock";
import { ContentBlockCard } from "@/modules/hub/features/ResourceLibraryV2/components/ContentBlockCard";
import { PlayCircle, FileText, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export const BLOCK_TYPES = {
  text: "text",
  image: "image",
  file: "file",
  link: "link",
  video: "video",
  audio: "audio",
  resource_reference: "resource_reference",
  faq: "faq",
  columns: "columns",
  quote: "quote",
  heading: "heading",
} as const;

export type BlockType = keyof typeof BLOCK_TYPES;

export interface ReferencedResource {
  // Resource fields
  id: number;
  uuid: string;
  slug: string;
  title: string;
  description?: string | null;
  thumbnailUrl?: string | null;
  resourceType?: string | null;
  sortOrder: number;
}

export interface ContentBlockBase {
  id?: string;
  type: BlockType;
  content: string;
  fileId?: number | null;
  order_number?: number;
  title?: string;
  shortDescription?: string;
  short_description?: string;
  file?: {
    id: number;
    uuid: string;
    url: string | null;
    name: string | null;
    contentType: string | null;
    size: number | null;
  } | null;
  referenced_resources?: ReferencedResource[] | null;
}

// Content type interfaces for structured blocks
export interface FaqItem {
  question: string;
  answer: string;
}

export interface FaqBlockContent {
  items: FaqItem[];
}

export interface ColumnsBlockContent {
  leftColumn: string;
  rightColumn: string;
}

export interface QuoteBlockContent {
  text: string;
  attribution?: string;
}

export interface HeadingBlockContent {
  text: string;
  level: "h2" | "h3" | "h4" | "h5" | "h6";
}

interface ResourceDetailsContentBlockProps {
  block: ContentBlockBase;
}

export default function ResourceDetailsContentBlock({
  block,
}: ResourceDetailsContentBlockProps) {
  // Backwards compatibility - use short_description if shortDescription not available
  const description = block.shortDescription || block.short_description;

  switch (block.type) {
    case "text":
      return (
        <div className="my-6">
          <div className="resource-markdown prose dark:prose-invert max-w-none">
            <ReactMarkdown
              components={{
                // All links in resource content open in new tab
                a: ({ node, children, href, ...props }) => (
                  <a
                    href={href}
                    rel="noopener noreferrer"
                    target="_blank"
                    {...props}
                  >
                    {children}
                  </a>
                ),
              }}
              remarkPlugins={[remarkGfm]}
            >
              {block.content}
            </ReactMarkdown>
          </div>
        </div>
      );

    case "image": {
      // Use file URL if available, fallback to content
      const imageUrl = block.file?.url || block.content;

      if (!imageUrl) {
        return (
          <div className="my-6 p-4 border rounded-md bg-gray-50 dark:bg-slate-800 dark:border-slate-700">
            <p className="text-gray-500 dark:text-gray-400">
              Image not available
            </p>
          </div>
        );
      }

      return (
        <div className="my-6">
          {block.title && (
            <h3 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">
              {block.title}
            </h3>
          )}
          {description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {description}
            </p>
          )}
          <img
            alt={block.title || block.file?.name || "Resource image"}
            className="object-cover w-full h-auto"
            src={imageUrl}
          />
        </div>
      );
    }

    case "file": {
      // Use file URL if available, fallback to content
      const fileUrl = block.file?.url || block.content;
      const fileName = block.file?.name || undefined;
      const title = block.title || fileName;

      if (!fileUrl) {
        return (
          <div className="my-6 p-4 border rounded-md bg-gray-50 dark:bg-slate-800 dark:border-slate-700">
            <p className="text-gray-500 dark:text-gray-400">
              File not available
            </p>
          </div>
        );
      }

      // Get file type for badge
      const getFileType = () => {
        const contentType = block.file?.contentType;
        if (contentType?.includes("pdf")) return "PDF";
        if (contentType?.startsWith("image/")) return "Image";
        if (contentType?.startsWith("video/")) return "Video";
        const ext = fileUrl.split(".").pop()?.toUpperCase();
        return ext || "File";
      };

      // Format file size
      const formatFileSize = (bytes: number): string => {
        if (!bytes || bytes === 0) return "";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
      };

      const fileSize = block.file?.size
        ? formatFileSize(block.file.size)
        : null;

      return (
        <ContentBlockCard
          action={
            <a
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              download
              href={fileUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </a>
          }
          badge={
            <div className="flex items-center gap-2">
              <Badge className="text-xs" variant="outline">
                {getFileType()}
              </Badge>
              {fileSize && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {fileSize}
                </span>
              )}
            </div>
          }
          className="my-6"
          description={description}
          icon={<FileText className="w-6 h-6" />}
          title={title}
        />
      );
    }

    case "video":
      // Only wrap in ContentBlockCard if there's a title or description
      if (block.title || description) {
        return (
          <ContentBlockCard
            badge={
              <Badge className="text-xs" variant="outline">
                Video
              </Badge>
            }
            className="my-6"
            description={description}
            icon={<PlayCircle className="w-6 h-6" />}
            noPadding
            title={block.title}
          >
            <div className="relative aspect-video overflow-hidden bg-black rounded-b-lg">
              <VideoEmbed contentUrl={block.content.trim()} />
            </div>
          </ContentBlockCard>
        );
      }
      // No wrapper if no title/description
      return (
        <div className="my-6">
          <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
            <VideoEmbed contentUrl={block.content.trim()} />
          </div>
        </div>
      );

    case "link":
      return (
        <LinkEmbed
          contentUrl={block.content.trim()}
          description={description}
          title={block.title ?? block.content.trim()}
        />
      );

    case "audio": {
      const audioUrl = block.content.trim();
      const hasMetadata = !!(block.title || description);

      // Handle Transistor URLs
      if (audioUrl.includes("transistor.fm")) {
        const match = audioUrl.match(/\/[es]\/([a-z0-9]+)/i);
        const episodeId = match ? match[1] : "";

        if (episodeId) {
          const transistorEmbed = (
            <iframe
              className={hasMetadata ? "rounded-b-lg" : "rounded-lg"}
              frameBorder="0"
              height="180"
              scrolling="no"
              seamless
              src={`https://share.transistor.fm/e/${episodeId}`}
              width="100%"
            />
          );

          if (hasMetadata) {
            return (
              <ContentBlockCard
                className="my-6"
                description={description}
                noPadding
                title={block.title}
              >
                {transistorEmbed}
              </ContentBlockCard>
            );
          }
          return <div className="my-6">{transistorEmbed}</div>;
        }
      }

      // Handle Spotify URLs
      if (audioUrl.includes("spotify.com")) {
        const episodeId = audioUrl.split("/").pop() || "";
        const spotifyEmbed = (
          <iframe
            allow="encrypted-media"
            className={hasMetadata ? "rounded-b-lg" : "rounded-lg"}
            frameBorder="0"
            height="232"
            src={`https://open.spotify.com/embed/episode/${episodeId}`}
            width="100%"
          />
        );

        if (hasMetadata) {
          return (
            <ContentBlockCard
              className="my-6"
              description={description}
              noPadding
              title={block.title}
            >
              {spotifyEmbed}
            </ContentBlockCard>
          );
        }
        return <div className="my-6">{spotifyEmbed}</div>;
      }

      // Default to HTML5 audio player for MP3 and other direct audio files
      const html5Audio = (
        <div
          className={
            hasMetadata
              ? "bg-gray-100 dark:bg-slate-800 rounded-b-lg p-4"
              : "bg-gray-100 dark:bg-slate-800 rounded-lg p-4"
          }
        >
          <audio className="w-full" controls>
            <source src={audioUrl} />
            Your browser does not support the audio element.
          </audio>
        </div>
      );

      if (hasMetadata) {
        return (
          <ContentBlockCard
            className="my-6"
            description={description}
            noPadding
            title={block.title}
          >
            {html5Audio}
          </ContentBlockCard>
        );
      }
      return <div className="my-6">{html5Audio}</div>;
    }

    case "resource_reference": {
      if (
        !block.referenced_resources ||
        block.referenced_resources.length === 0
      ) {
        return null;
      }

      return (
        <ResourceReferenceBlock
          blockDescription={description}
          blockTitle={block.title}
          references={block.referenced_resources}
        />
      );
    }

    case "faq":
      return <FaqBlock content={block.content} />;

    case "columns":
      return <ColumnsBlock content={block.content} />;

    case "quote":
      return <QuoteBlock content={block.content} />;

    case "heading":
      return <HeadingBlock content={block.content} />;

    default:
      return null;
  }
}
